const REMOTE_ETABLISSEMENTS_API_URL = 'https://carte.minesursi.gouv.cd/api/etablissements?page=1&per_page=1600';
const LOCAL_ETABLISSEMENTS_API_URL = '/api-carte/api/etablissements?page=1&per_page=1600';
const ETABLISSEMENTS_API_URL = (
  process.env.REACT_APP_ETABLISSEMENTS_API_URL ||
  (process.env.NODE_ENV === 'development' ? LOCAL_ETABLISSEMENTS_API_URL : REMOTE_ETABLISSEMENTS_API_URL)
);

export class EtablissementsDataError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'EtablissementsDataError';
    this.code = code;
  }
}

export const asArray = (payload, keys = []) => {
  if (Array.isArray(payload)) return payload;
  for (const key of keys) {
    if (Array.isArray(payload?.[key])) return payload[key];
  }
  if (Array.isArray(payload?.results)) return payload.results;
  return [];
};

export const getEtablissementTypeFromCodstat = (codstat) => {
  if (Number(codstat) === 1) return 'Public';
  if (Number(codstat) === 2) return 'Privé';

  return '';
};

export const normalizeEtablissement = (etablissement) => {
  const sigle = String(etablissement?.sigle_etab || etablissement?.sigle || etablissement?.code || '').trim();
  const denomination = String(
    etablissement?.denom_etabl ||
    etablissement?.nom_etablissement ||
    etablissement?.name ||
    ''
  ).trim();
  const typeEtablissement = getEtablissementTypeFromCodstat(etablissement?.codstat);

  if (!denomination) return null;

  return {
    id: etablissement?.id,
    sigle_etab: sigle,
    denom_etabl: denomination,
    code: sigle,
    name: denomination,
    type_etablissement: typeEtablissement,
    type_etablissment: typeEtablissement,
  };
};

export const findEtablissementByValue = (etablissements, value) => {
  const normalizedValue = String(value || '').trim();
  if (!normalizedValue) return null;

  return etablissements.find((etablissement) => (
    etablissement.denom_etabl === normalizedValue ||
    etablissement.sigle_etab === normalizedValue
  )) || null;
};

export const buildUniversityOption = (uni) => {
  const sigle = uni.sigle_etab || uni.code;
  const denomination = uni.denom_etabl || uni.name;

  return {
    value: denomination,
    label: sigle ? `${denomination} | ${sigle}` : denomination,
    sigle,
    type_etablissement: uni.type_etablissement || uni.type_etablissment || '',
  };
};

export const getEtablissementsErrorMessage = (error) => {
  if (error?.name !== 'EtablissementsDataError') {
    return "Impossible de charger la liste des établissements.";
  }

  if (error.code === 'empty') {
    return "Aucun établissement n'est disponible.";
  }

  return error.message || "Impossible de charger la liste des établissements.";
};

const buildEtablissementsUrl = (page) => {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost';
  const url = new URL(ETABLISSEMENTS_API_URL, baseUrl);
  url.searchParams.set('page', String(page));
  return url.toString();
};

const pickEtablissementFields = (etablissement) => ({
  denom_etabl: etablissement?.denom_etabl,
  sigle_etab: etablissement?.sigle_etab,
  codstat: etablissement?.codstat,
});

const fetchEtablissementsPage = async (page, signal) => {
  const response = await fetch(buildEtablissementsUrl(page), {
    headers: {
      Accept: 'application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new EtablissementsDataError('request', `Impossible de charger les établissements (${response.status}).`);
  }

  return response.json();
};

export const fetchAllEtablissements = async (signal) => {
  const firstPayload = await fetchEtablissementsPage(1, signal);
  const totalPages = Number(firstPayload?.meta?.total_pages || 1);
  const remainingPages = Array.from({ length: Math.max(totalPages - 1, 0) }, (_, index) => index + 2);
  const remainingPayloads = await Promise.all(
    remainingPages.map((page) => fetchEtablissementsPage(page, signal))
  );
  const etablissements = [firstPayload, ...remainingPayloads]
    .flatMap((payload) => asArray(payload, ['data', 'etablissements']))
    .map(pickEtablissementFields);

  if (!etablissements.length) {
    throw new EtablissementsDataError('empty', 'Aucun établissement disponible.');
  }

  return etablissements;
};
